import axios from "axios";
import {
  BettingAccount,
  Transaction,
  User,
  ApiResponse,
  PaymentSettings,
  RechargeCouponCheckResult,
  RechargeCouponValidateResult,
  LotterySegment,
  LotteryStatus,
  SpinResult,
  LotteryHistorySpin,
  LotteryClaimResult,
} from "@/types";
import { AppSettingResponse } from "@/types/app";
import { alertService } from "./alertService";
import { directus } from "@/lib/directus";
import { customEndpoint } from "@directus/sdk";

const API_BASE_URL = "https://ftx-pay.futurix.xyz";
// const API_BASE_URL = "http://localhost:8055"; // Point to local Directus for development

// Configuration Axios comme dans le projet Flutter avec Dio
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add response interceptor for error handling
// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 errors are handled by ProtectedRoute component
    // No automatic redirect to prevent reload loops on public pages
    if (error.response?.status === 401) {
      console.debug('🔒 401 Unauthorized - letting ProtectedRoute handle redirect');
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // User management
  async createUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response = await api.post("/users", userData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create user",
      };
    }
  },

  async getUser(userId: string): Promise<ApiResponse<User>> {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get user",
      };
    }
  },

  // Betting accounts
  async getBettingAccounts(
    userId: string
  ): Promise<ApiResponse<BettingAccount[]>> {
    try {
      const response = await api.get(`/users/${userId}/accounts`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to get betting accounts",
      };
    }
  },

  async createBettingAccount(
    accountData: Omit<BettingAccount, "id">
  ): Promise<ApiResponse<BettingAccount>> {
    try {
      const response = await api.post("/accounts", accountData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to create betting account",
      };
    }
  },

  // Balance checking
  async checkBalance(
    accountId: string
  ): Promise<ApiResponse<{ balance: number }>> {
    try {
      const response = await api.get(`/accounts/${accountId}/balance`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to check balance",
      };
    }
  },

  // Transactions
  async createTransaction(
    transactionData: Omit<Transaction, "id" | "createdAt" | "updatedAt">
  ): Promise<ApiResponse<Transaction>> {
    try {
      const response = await api.post("/transactions", transactionData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to create transaction",
      };
    }
  },

  async getTransactions(
    userId: string,
    type?: string
  ): Promise<ApiResponse<Transaction[]>> {
    try {
      const params = type ? { type } : {};
      const response = await api.get(`/users/${userId}/transactions`, {
        params,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get transactions",
      };
    }
  },

  async updateTransactionStatus(
    transactionId: string,
    status: Transaction["status"]
  ): Promise<ApiResponse<Transaction>> {
    try {
      const response = await api.patch(`/transactions/${transactionId}`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update transaction",
      };
    }
  },

  // Affiliation/Referral
  async getReferralInfo(
    userId: string
  ): Promise<
    ApiResponse<{
      referralCode: string;
      referredUsers: User[];
      totalEarnings: number;
    }>
  > {
    try {
      const response = await api.get(`/users/${userId}/referral`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get referral info",
      };
    }
  },

  async processReferralWithdrawal(
    userId: string,
    amount: number
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/users/${userId}/referral-withdraw`, {
        amount,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "Failed to process referral withdrawal",
      };
    }
  },

  // Payment settings
  async getPaymentSettings(): Promise<ApiResponse<PaymentSettings>> {
    try {
      const response = await api.get("/settings/payment");
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to get payment settings",
      };
    }
  },

  // Coupons
  async uploadCoupon(
    userId: string,
    imageData: FormData
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/users/${userId}/coupons`, imageData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to upload coupon",
      };
    }
  },

  async getCoupons(userId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get(`/users/${userId}/coupons`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to get coupons",
      };
    }
  },

  // Update user endpoint
  async updateUser(
    userId: string,
    userData: Partial<User>
  ): Promise<ApiResponse<User>> {
    try {
      const response = await api.patch(`/users/${userId}`, userData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to update user",
      };
    }
  },

  async checkRechargeCoupon(code: string): Promise<RechargeCouponCheckResult> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/recharge-coupon/check",
        method: "POST",
        body: JSON.stringify({ code }),
      }));
      return response as RechargeCouponCheckResult;
    } catch (error: any) {
      return {
        valid: false,
        error: error.response?.data?.error || "Code coupon invalide",
      };
    }
  },

  async validateRechargeCoupon(code: string, userId: string, accountId: string): Promise<RechargeCouponValidateResult> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/recharge-coupon/validate",
        method: "POST",
        body: JSON.stringify({ code, userId, accountId }),
      }));
      return response as RechargeCouponValidateResult;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || "Erreur lors de la validation",
      };
    }
  },

  async getMyRechargeCoupons(): Promise<{ coupons: any[] }> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/recharge-coupon/my-coupons",
        method: "GET",
      }));
      return response as { coupons: any[] };
    } catch (error: any) {
      return { coupons: [] };
    }
  },

  // Get lottery status for current user
  async getLotteryStatus(): Promise<LotteryStatus> {
    try {


      const response = await directus.request(customEndpoint({
        path: "/api/lottery/status",
        method: "GET",
      }))

      return response as LotteryStatus;
    } catch (error: any) {
      return { spinsAvailable: 0, canSpin: false };
    }
  },

  // Claim daily free spin
  async claimDailyLotterySpin(): Promise<LotteryClaimResult> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/lottery/claim-daily",
        method: "POST",
      }))
      return response as LotteryClaimResult;
    } catch (error: any) {
      return {
        success: false,
        spinsAvailable: 0,
        alreadyClaimedToday: true,
        error: error.response?.data?.error || "Erreur lors de la réclamation",
      };
    }
  },

  // Get wheel configuration (segments)
  async getLotteryWheelConfig(): Promise<{ segments: LotterySegment[] }> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/lottery/wheel-config",
        method: "GET",
      }))
      // const response = await api.get("/api/lottery/wheel-config");
      return response as { segments: LotterySegment[] };
    } catch (error: any) {
      return { segments: [] };
    }
  },

  // Execute a lottery spin
  async lotterySpin(): Promise<SpinResult> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/lottery/spin",
        method: "POST",
      }))
      return response as SpinResult;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || "Erreur lors du tirage",
      };
    }
  },

  // Get lottery spin history
  async getLotteryHistory(): Promise<{ spins: LotteryHistorySpin[] }> {
    try {
      const response = await directus.request(customEndpoint({
        path: "/api/lottery/history",
        method: "GET",
      }))
      return response as { spins: LotteryHistorySpin[] };
    } catch (error: any) {
      return { spins: [] };
    }
  },
};

// App-specific API endpoints matching Flutter app exactly
export const appApi = {
  // Balance checking for deposits
  getBalance: async (compteId: string): Promise<{ balance: number }> => {
    try {
      const response = await api.post("/api/balance", {
        compteId: compteId,
      });
      return response.data;
    } catch (error) {
      console.error("Error checking balance:", error);
      throw error;
    }
  },

  // Check if account has sufficient balance for amount
  checkBalance: async (compteId: string, amount: number): Promise<boolean> => {
    try {
      const response = await api.post("/api/balance", {
        compteId: compteId,
      });
      const balance = response.data.balance as number;
      console.debug("✅ Balance check successful:", {
        compteId,
        balance,
        requestedAmount: amount,
      });

      // Return true if balance is sufficient, false otherwise
      if (balance < amount) {
        // Send alert for insufficient balance (like Flutter)
        alertService.sendCriticalAlert({
          title: "🚨 SOLDE INSUFFISANT",
          message: `Solde actuel: ${balance} FCFA. Montant demandé: ${amount} FCFA.`,
          context: {
            current_balance: balance,
            requested_amount: amount,
          },
        });
      }

      return balance > amount;
    } catch (error: any) {
      console.error("❌ Error checking balance:", error);

      // Send alert for API failure (like Flutter)
      alertService.sendCriticalAlert({
        title: "🚨 ECHEC DE VERIFICATION DE SOLDE",
        message: `Erreur lors de la vérification du solde. Erreur: ${error.message || error
          }`,
        context: {
          error: error.toString(),
        },
      });

      // Throw error to trigger catch block in caller (like Flutter)
      throw error;
    }
  },

  // Check balance for withdrawals (specific endpoint)
  checkWithdraw: async (
    apiKey: string,
    appId: string
  ): Promise<{ solde: number }> => {
    try {
      const response = await api.post("/pay/appbalance", {
        api_key: apiKey,
        app_id: appId,
      });
      return response.data;
    } catch (error) {
      console.error("Error checking withdraw balance:", error);
      throw error;
    }
  },

  // Validate account ID and get user name
  getUserName: async (
    userId: string,
    compteId: string
  ): Promise<string | null> => {
    try {
      const response = await api.post("/api/username", {
        userId: userId,
        compteId: compteId,
      });
      const userName = response?.data?.userName ?? null;

      console.debug("✅ User validation successful:", {
        userId,
        compteId,
        userName,
      });

      // Return empty string for invalid accounts like in Flutter
      if (userName === "undefined undefined undefined" || userName === null) {
        return null;
      }

      return userName;
    } catch (error: any) {
      console.error("❌ Error validating user:", error);

      // SI on a déjà une réponse succès mais timeout après, on utilise la réponse
      if (error.response?.data?.userName) {
        const userName = error.response.data.userName;
        console.debug("🔄 Using cached successful response:", { userName });
        return userName;
      }

      // Pas de fallback mocké - on retourne une chaîne vide en cas d'erreur
      // comme dans le code Flutter d'origine
      console.debug("❌ API unavailable - returning empty string");
      return null;
    }
  },

  // Create payment intent for mobile money
  createPaymentIntent: async (data: {
    api_key: string;
    app_id: string;
    mob_app_id: string;
    amount: number;
    phonenumber: string;
    userID: string;
    commercial_id?: string;
  }): Promise<{ paymentIntent: string }> => {
    try {
      const response = await api.post("/pay/easy-service/intent", data);
      return response.data;
    } catch (error) {
      console.error("Error creating payment intent:", error);
      throw error;
    }
  },

  // Check payment status
  checkPaymentStatus: async (paymentIntentId: string): Promise<any> => {
    try {
      const response = await api.get(`/items/session/${paymentIntentId}`);
      return response.data;
    } catch (error) {
      console.error("Error checking payment status:", error);
      throw error;
    }
  },

  // Validate phone number format and detect operator
  validatePhoneNumber: (
    phone: string
  ): { valid: boolean; operator?: "orange" | "moov"; error?: string } => {
    // Remove any non-digit characters
    const cleanPhone = phone.replace(/\D/g, "");

    // Check if it's exactly 8 digits (Burkina Faso format)
    if (cleanPhone.length !== 8) {
      return { valid: false, error: "Le numéro doit contenir 8 chiffres" };
    }

    const prefix = cleanPhone.substring(0, 2);

    const orangePrefixes = [
      "04",
      "05",
      "06",
      "07",
      "44",
      "54",
      "55",
      "56",
      "57",
      "64",
      "65",
      "66",
      "67",
      "74",
      "75",
      "76",
      "77",
    ];

    const moovPrefixes = [
      "01",
      "02",
      "03",
      "50",
      "51",
      "52",
      "53",
      "60",
      "61",
      "62",
      "63",
      "70",
      "71",
      "72",
      "73",
    ];

    if (orangePrefixes.includes(prefix)) {
      return { valid: true, operator: "orange" };
    } else if (moovPrefixes.includes(prefix)) {
      return { valid: true, operator: "moov" };
    } else {
      return {
        valid: false,
        error: "Veuillez entrer un numéro valide Orange ou Moov",
      };
    }
  },
};

export default api;
